import { BackArrowIcon, CopyIcon } from "@/assets/icons/admin";
import { useEditReferral, useGetSpecialReferrals } from "@/services/admin.service";
import { SpecialRefEditRequest, SpecialReferralDetails } from "@/types";
import { Button, Input, Textarea } from "@nextui-org/react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";

interface RefPlayerDetails {
    onCancel?: () => void;
    item: Partial<SpecialReferralDetails>
}

const RefPlayerDetailTab: React.FC<RefPlayerDetails> = ({ item, onCancel }) => {
    const [id, setId] = useState(item.id);
    const [userName, setUserName] = useState(item.userName);
    const [fullName, setFullName] = useState(item.fullName);
    const [createdAt, setCreatedAt] = useState(item.createdAt);
    const [refLink, setRefLink] = useState(item.refLink);
    const [specialRate, setSpecialRate] = useState(item.specialRate);
    const [reason, setReason] = useState(item.reason);

    useEffect(() => {
        setId(item.id)
        setUserName(item.userName)
        setFullName(item.fullName)
        setCreatedAt(item.createdAt)
        setRefLink(item.refLink)
        setSpecialRate(item.specialRate)
        setReason(item.reason)
    }, [item]);

    const handleCopy = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            console.log(`Copied: ${text}`);
            toast.success('Copied text')
        }).catch((err) => {
            console.error('Failed to copy: ', err);
            toast.error('Error when copying text')
        });
    };

    const formatDateTime = (isoString: string | number | Date) => {
        const date = new Date(isoString);

        const padZero = (num: number) => (num < 10 ? "0" : "") + num;

        const day = padZero(date.getDate());
        const month = padZero(date.getMonth() + 1); // getMonth() returns month from 0 to 11
        const year = date.getFullYear(); // Get full year
        const hours = padZero(date.getHours());
        const minutes = padZero(date.getMinutes());
        const seconds = padZero(date.getSeconds()); // Add this line to get seconds

        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`; // Include seconds in the returned string
    };
    const { refetch } = useGetSpecialReferrals({ page: 1, pageSize: 10 });

    const handleUpdateReferral = useEditReferral()
    const handleSave = async () => {
        const updateData: SpecialRefEditRequest = {
            rate: specialRate ?? 0,
            reason: reason ?? "",
        }
        handleUpdateReferral.mutate(
            {
                id: id ?? "",
                data: updateData
            },
            {
                onSuccess: (res) => {
                    console.log("Updated referral value");
                    refetch();
                    toast.success("Updated referral value");
                },
                onError: (error: any) => {
                    console.log("Failed to update referral value");
                    console.log(error);
                    toast.error("Failed to update referral value");
                },
            },
        )
    }

    const handleSpecialRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Convert input value to a number
        const value = parseFloat(e.target.value);
        setSpecialRate(isNaN(value) ? 0 : value); // Handle case where conversion fails
    };

    return (
        <>
            <div>
                <div className="font-ubuntu mb-4 w-full text-center text-2xl items-center font-semibold text-white sm:float-left">
                    <span>
                        <Button
                            isIconOnly
                            onClick={onCancel}
                            className="transparent-bg sm:pl-2 float-left ml-[-11px] h-auto w-auto"
                        >
                            <BackArrowIcon />
                        </Button>
                        <p className="float-left text-xl">
                            Player Detail
                        </p>
                    </span>
                </div>
                {/* Details component */}
                <div className="flex-col w-full py-2 sm:p-2">
                    <div className="w-full flex flex-col sm:flex-row items-center justify-evenly p-0">
                        <div className="w-full sm:w-1/2 py-2 sm:py-4">
                            <label
                                htmlFor="firstName"
                                className="field-label mb-2 block text-base font-normal text-white"
                            >
                                Username
                            </label>
                            <div className="flex items-center justify-between">
                                <span className="text-[#1181E8] font-openSans font-bold text-lg">
                                    {userName}
                                </span>
                            </div>
                        </div>
                        <div className="w-full sm:w-1/2 py-2 sm:py-4">
                            <label
                                htmlFor="firstName"
                                className="field-label mb-2 block text-base font-normal text-white"
                            >
                                Full Name
                            </label>
                            <div className="flex items-center justify-between">
                                <span className="text-white font-openSans font-bold text-lg">
                                    {fullName}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="w-full flex flex-col sm:flex-row items-center justify-evenly p-0">
                        <div className="w-full sm:w-1/2 py-2 sm:py-4">
                            <label
                                htmlFor="firstName"
                                className="field-label mb-2 block text-base font-normal text-white"
                            >
                                Approved Date
                            </label>
                            <div className="flex items-center justify-between">
                                <span className="text-white font-openSans font-bold text-lg">
                                    {formatDateTime(createdAt ?? "")}
                                </span>
                            </div>
                        </div>
                        <div className="w-full sm:w-1/2 py-2 sm:py-4">
                            <label
                                htmlFor="firstName"
                                className="field-label mb-2 block text-base font-normal text-white"
                            >
                                Ref. Links
                            </label>
                            <div className="flex items-center">
                                <span className="text-white font-openSans font-bold text-lg">
                                    {refLink}
                                </span>
                                <span className="">
                                    <Button
                                        isIconOnly
                                        onClick={() => handleCopy(refLink ?? "")}
                                        className="transparent-bg h-6 w-6"
                                    >
                                        <CopyIcon />
                                    </Button>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Input form */}
                <div className="flex w-full flex-col pt-2">
                    <div className="flex flex-col">
                        <div className="mt-1 basis-full sm:mt-0 sm:basis-full">
                            <label
                                htmlFor="firstName"
                                className="field-label mb-2 block text-sm font-normal text-white"
                            >
                                Special Rates (%)
                            </label>
                            <Input
                                type="number"
                                className={`w-full rounded-md border border-solid sm:w-full input-bg !cursor-text`}
                                classNames={{
                                    inputWrapper: [
                                        "!cursor-text",
                                        "bg-white-5",
                                        "text-sm"
                                    ],
                                }}
                                placeholder="Type Here"
                                value={specialRate != null ? specialRate.toString() : ""}
                                onChange={handleSpecialRateChange}
                            ></Input>
                        </div>
                        <div className="basis-full mt-4 sm:basis-full">
                            <label
                                htmlFor="firstName"
                                className="field-label mb-2 block text-sm font-normal text-white"
                            >
                                Reason
                            </label>
                            <Textarea
                                value={reason}
                                className={`input-bg !cursor-text rounded-md border border-solid`}
                                classNames={{
                                    inputWrapper: [
                                        "!cursor-text",
                                        "bg-white-5",
                                        "reason-text-field-height", "text-sm"
                                    ],
                                }}
                                variant="bordered"
                                defaultValue={"Influencer"}
                                placeholder="Type Here"
                                onChange={(e) => setReason(e.target.value)}
                            />
                        </div>
                        <div className="mt-4 basis-1/2 ">
                            <div className="sm:float-right">
                                <Button onClick={handleSave} className="admin-common-button-gradient font-ubuntu w-full rounded-md pl-20 pr-20 text-base font-bold">
                                    Save
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default RefPlayerDetailTab;